import { BACKEND } from '../config';
import { reportSessionExpired } from '../utils/sessionExpiry';

let csrfMemory = '';
let csrfInitPromise: Promise<void> | null = null;

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS', 'TRACE']);

const getCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  const part = document.cookie
    .split(';')
    .map((chunk) => chunk.trim())
    .find((chunk) => chunk.startsWith(`${name}=`));
  if (!part) return null;
  const value = part.slice(name.length + 1);
  return value ? decodeURIComponent(value) : null;
};

const needsCsrf = (method = 'GET') => !SAFE_METHODS.has(method.toUpperCase());

const ensureCsrf = async (force = false): Promise<void> => {
  if (csrfMemory && !force) return;
  if (!csrfInitPromise) {
    csrfInitPromise = fetch(`${BACKEND}/api/csrf/`, { credentials: 'include' })
      .then(async (res) => {
        const payload = await res.json().catch(() => ({}));
        csrfMemory = payload?.csrftoken || payload?.csrfToken || getCookie('csrftoken') || '';
      })
      .catch(() => {})
      .finally(() => {
        csrfInitPromise = null;
      });
  }
  await csrfInitPromise;
};

export const primeCsrf = async (): Promise<void> => {
  await ensureCsrf();
};

// Токен из памяти — для csrfFetch/safeFetch, когда cookie недоступна (desktop-EXE на file://).
// Заполняется через primeCsrf()/ensureCsrf() из тела /api/csrf/.
export const getCsrfToken = (): string => csrfMemory;

const parseApiError = async (res: Response): Promise<string> => {
  try {
    const j = await res.clone().json();
    return (j?.detail || j?.error || j?.reason) ?? JSON.stringify(j);
  } catch {
    return (await res.text().catch(() => `${res.status}`)) || `${res.status}`;
  }
};

const request = async <T>(url: string, opts?: RequestInit, isForm = false): Promise<T> => {
  const method = (opts?.method || 'GET').toUpperCase();

  if (needsCsrf(method) && !csrfMemory) {
    await ensureCsrf();
  }

  const headers: Record<string, string> = {
    'X-Requested-With': 'XMLHttpRequest',
    ...(isForm ? {} : { 'Content-Type': 'application/json' }),
    ...(opts?.headers as Record<string, string> | undefined),
  };

  if (needsCsrf(method) && csrfMemory) headers['X-CSRFToken'] = csrfMemory;

  const doFetch = () =>
    fetch(url, {
      credentials: 'include',
      ...opts,
      headers,
    });

  let res = await doFetch();

  if (res.status === 403) {
    const text = await res.clone().text().catch(() => '');
    if (text.toLowerCase().includes('csrf')) {
      await ensureCsrf(true);
      if (csrfMemory) {
        headers['X-CSRFToken'] = csrfMemory;
        res = await doFetch();
      }
    }
  }

  if (res.status === 401 && !url.includes('/api/login')) {
    // Истёкшая сессия — глобальный репорт (см. utils/sessionExpiry.ts)
    reportSessionExpired('apiClient:' + url.slice(0, 80));
  }

  if (!res.ok) {
    throw new Error(await parseApiError(res));
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
};

export const apiJson = <T = unknown>(url: string, opts?: RequestInit) => request<T>(url, opts, false);
export const apiForm = <T = unknown>(url: string, opts?: RequestInit) => request<T>(url, opts, true);
